import type { CoinSymbol, Transaction } from '@asia/shared';
import { rpc } from './ws';

/** A coin that can be bought with fiat, and what the provider will accept. */
export interface OnrampAsset {
  coin: string;
  chain: string;
  label: string;
  minAmount: number | null;
  maxAmount: number | null;
}

export interface OnrampCatalogue {
  fiatCurrency: string;
  paymentModes: string[];
  assets: OnrampAsset[];
  /** Provider test contour: payment can be simulated. Never true in production. */
  stage?: boolean;
}

export interface OnrampFee {
  type: string;
  amount: number;
  currency: string;
}

/** What a purchase costs. The rate and commission are the provider's. */
export interface OnrampQuote {
  coin: string;
  chain: string;
  fiatCurrency: string;
  fiatAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  paymentMode: string;
  fees: OnrampFee[];
  totalFee: number;
  commissionPercent: string;
  quoteSignature: string;
  expiresIn: number;
}

export interface OnrampCustomer {
  started: boolean;
  phoneSet: boolean;
  needsOtp: boolean;
  verificationLevel: number;
  kycStatus: 'none' | 'pending' | 'approved' | 'rejected';
  identified: boolean;
}

export interface OnrampOrder {
  id: string;
  coin: string;
  chain: string;
  walletAddress: string;
  fiatCurrency: string;
  fiatAmount: number;
  cryptoAmount: number | null;
  providerFeeUsd: number | null;
  paymentMode: string;
  status: 'created' | 'processing' | 'completed' | 'failed' | 'expired' | 'cancelled';
  /** The payment went through and USDT is on its way to the wallet. */
  paid?: boolean;
  txHash: string | null;
  checkoutUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export const onrampAssetsApi = () => rpc<OnrampCatalogue>('onramp.assets');

export const onrampCustomerApi = () => rpc<{ customer: OnrampCustomer }>('onramp.customer').then((r) => r.customer);

export const onrampStartPhoneApi = (phone: string) =>
  rpc<{ customer: OnrampCustomer; smsSent: boolean }>('onramp.startPhone', { phone });

export const onrampVerifyPhoneApi = (otp: string) =>
  rpc<{ customer: OnrampCustomer }>('onramp.verifyPhone', { otp }).then((r) => r.customer);

export const onrampResendOtpApi = () => rpc<{ customer: OnrampCustomer }>('onramp.resendOtp');

export const onrampIdentifyApi = (input: {
  firstName: string;
  lastName: string;
  patronymic?: string;
  birthDate: string;
  docNumber: string;
}) => rpc<{ customer: OnrampCustomer }>('onramp.identify', input).then((r) => r.customer);

export const onrampQuoteApi = (amount: string) => rpc<OnrampQuote>('onramp.quote', { amount });

export const onrampCreateApi = (params: { walletId: string; amount: string; quoteSignature: string }) =>
  rpc<{ order: OnrampOrder }>('onramp.create', params).then((r) => r.order);

export const onrampListApi = (limit?: number) =>
  rpc<{ orders: OnrampOrder[] }>('onramp.list', { limit }).then((r) => r.orders);

export const onrampGetApi = (id: string) =>
  rpc<{ order: OnrampOrder }>('onramp.get', { id }).then((r) => r.order);

export const onrampSimulatePaidApi = (id: string) =>
  rpc<{ order: OnrampOrder }>('onramp.simulatePaid', { id }).then((r) => r.order);

/** A finished purchase reads as an incoming "bought" row; one still settling
 *  shows as pending so it doesn't vanish while the user waits for it. */
export function onrampToTransaction(o: OnrampOrder): Transaction {
  const amount = o.cryptoAmount ?? 0;
  return {
    id: `onramp:${o.id}`,
    type: 'buy',
    status: o.status === 'completed' ? 'confirmed' : o.status === 'failed' || o.status === 'expired' || o.status === 'cancelled' ? 'failed' : 'pending',
    coin: (o.coin === 'TRX' ? 'TRX' : 'USDT') as CoinSymbol,
    chain: 'TRON',
    amount,
    amountUsd: o.fiatCurrency === 'USD' ? o.fiatAmount : 0,
    feeUsd: o.providerFeeUsd ?? 0,
    date: o.createdAt,
    ...(o.txHash ? { explorerUrl: `https://tronscan.org/#/transaction/${o.txHash}` } : {}),
  };
}
